import { Box, Mark, TextInput } from "@mantine/core";
import { useState } from "react";
import type { ApiResponse } from "../../../../../api";
import api from "../../../../../api";
import urls from "../../../../../urls";
import type {
  ProjectMinimal,
  VariantMinimal
} from "../../../../../utils/backend-types";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal/DeleteConfirmationModal";

export enum ActionObject {
  Project = "Projekt",
  Variant = "Variante"
}

type ActionConfirmationModalProps<T> = {
  onActionSuccess: (data: T | null) => void;
  onClose: () => void;
  onError: (error: unknown) => void;
} & (
  | {
      object: ActionObject.Project;
      project: ProjectMinimal;
    }
  | {
      object: ActionObject.Variant;
      variant: VariantMinimal;
    }
);

function ActionConfirmationModal<T>({
  onActionSuccess,
  onClose,
  onError,
  ...objectProps
}: ActionConfirmationModalProps<T>) {
  const [disableSuccessButton, setDisableSuccessButton] = useState(
    objectProps.object === ActionObject.Project
  );
  const [loading, setLoading] = useState(false);

  async function handleAction() {
    setLoading(true);

    let response: ApiResponse<T> | null = null;

    try {
      if (objectProps.object === ActionObject.Project) {
        response = await api.delete<T>(
          urls.api.projectsDetail(objectProps.project.id)
        );
      } else if (objectProps.object === ActionObject.Variant) {
        response = await api.delete<T>(
          urls.api.variantDetail(objectProps.variant.id)
        );
      }

      onActionSuccess(response ? response.data : null);
    } catch (error) {
      onError(error);
    }

    onClose();
  }

  function verifyProjectName(projectName: string) {
    if (objectProps.object !== ActionObject.Project) {
      console.error("verifyProjectName called for non-project object");
      return;
    }

    setDisableSuccessButton(projectName !== objectProps.project.name);
  }

  let modalBodyElement: React.ReactNode | null = null;

  if (objectProps.object === ActionObject.Project) {
    modalBodyElement = (
      <DeleteProjectModalBodyElement
        projectName={objectProps.project.name}
        onVerificationTextChange={verifyProjectName}
      />
    );
  } else if (objectProps.object === ActionObject.Variant) {
    modalBodyElement = (
      <DeleteVariantModalBodyElement variantName={objectProps.variant.name} />
    );
  }

  return (
    <DeleteConfirmationModal
      headerName={objectProps.object}
      isConfirmationBtnDisabled={disableSuccessButton}
      isLoading={loading}
      modalBodyElement={modalBodyElement}
      name={
        objectProps.object === ActionObject.Project
          ? objectProps.project.name
          : objectProps.variant.name
      }
      opened
      toggleModal={onClose}
      onConfirm={handleAction}
    />
  );
}

interface DeleteProjectModalBodyElementProps {
  projectName: string;
  onVerificationTextChange: (text: string) => void;
}

function DeleteProjectModalBodyElement({
  projectName,
  onVerificationTextChange
}: DeleteProjectModalBodyElementProps) {
  const [value, setValue] = useState("");

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
    onVerificationTextChange(event.target.value);
  }

  return (
    <div>
      <p>
        Sind Sie sicher, dass Sie dieses Projekt löschen möchten? Dieser Schritt
        kann nicht rückgängig gemacht werden.
      </p>
      <Box>
        Geben Sie zur Bestätigung den Namen des Projektes{" "}
        <Mark>{projectName}</Mark> in das Textfeld ein.
        <TextInput value={value} onChange={handleChange} />
      </Box>
    </div>
  );
}

function DeleteVariantModalBodyElement({
  variantName
}: {
  variantName: string;
}) {
  return (
    <div>
      <p>
        Sind Sie sicher, dass Sie <Mark>{variantName}</Mark> löschen möchten?
        Dieser Schritt kann nicht rückgängig gemacht werden.
      </p>
      <p>
        Damit werden auch verknüpfte Objekte, wie Personen und Erzeuger
        gelöscht.
      </p>
    </div>
  );
}

export { ActionConfirmationModal, ActionConfirmationModalProps };
